import { WarningIcon } from '@daily/shared/components/Icons/WarningIcon';
import { useSnackbar } from '@daily/shared/contexts/Snackbar';
import {
  useCPULoad,
  useLocalSessionId,
  useParticipantProperty,
} from '@daily-co/daily-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isTrackOff } from '/lib/participants';

import { useAudioProcessor } from './useAudioProcessor';
import { useBackgroundEffect } from './useBackgroundEffect';

const MIN_TIME_BETWEEN_NOTIFICATIONS_MS = 60 * 1000;

type NotificationType =
  | 'general'
  | 'encoding'
  | 'noiseCancellation'
  | 'backgroundEffect';

export const useHighCPUAlert = () => {
  const { t } = useTranslation();
  const { addMessage, handleHide, messages } = useSnackbar();

  const { currentEffect } = useBackgroundEffect();
  const { noiseCancellationOn } = useAudioProcessor();

  const localSessionId = useLocalSessionId();
  const videoState = useParticipantProperty(
    localSessionId,
    'tracks.video.state'
  );
  const isVideoEnabled = !isTrackOff(videoState);

  const lastNotificationTimeRef = useRef<number>(0);

  const [backgroundEffectsNotificationId, setBackgroundEffectsNotificationId] =
    useState<number | null>(null);
  const [noiseCancellationNotificationId, setNoiseCancellationNotificationId] =
    useState<number | null>(null);
  const [videoEncodingNotificationId, setVideoEncodingNotificationId] =
    useState<number | null>(null);

  const [ignoreNotificationTypes, setIgnoreNotificationTypes] = useState<
    Array<NotificationType>
  >([]);

  useEffect(
    function hideBackgroundEffectsSnackbar() {
      if (currentEffect !== 'none' || !backgroundEffectsNotificationId) return;
      handleHide(backgroundEffectsNotificationId);
      setBackgroundEffectsNotificationId(null);
    },
    [backgroundEffectsNotificationId, currentEffect, handleHide]
  );

  useEffect(
    function hideNoiseCancellationSnackbar() {
      if (noiseCancellationOn || !noiseCancellationNotificationId) return;
      handleHide(noiseCancellationNotificationId);
      setNoiseCancellationNotificationId(null);
    },
    [noiseCancellationNotificationId, noiseCancellationOn, handleHide]
  );

  useEffect(
    function hideVideoEncodingSnackbar() {
      if (isVideoEnabled || !videoEncodingNotificationId) return;
      handleHide(videoEncodingNotificationId);
      setVideoEncodingNotificationId(null);
    },
    [videoEncodingNotificationId, isVideoEnabled, handleHide]
  );

  const { reason, state } = useCPULoad();

  useEffect(() => {
    switch (state) {
      case 'high':
        let content = t('notification.highCpuUsage');
        let notificationType: NotificationType = 'general';

        if (reason === 'encode') {
          content = t('notification.highCpuUsageEncoding');
          notificationType = 'encoding';
        }
        if (noiseCancellationOn) {
          content = t('notification.highCpuUsageNoiseCancellation');
          notificationType = 'noiseCancellation';
        }
        if (currentEffect !== 'none') {
          content = t('notification.highCpuUsageBackgroundEffect');
          notificationType = 'backgroundEffect';
        }

        if (ignoreNotificationTypes.includes(notificationType)) return;

        const now = Date.now();
        const lastTime = lastNotificationTimeRef.current || 0;

        // Only show notification if enough time has passed since last notification of this type
        if (now - lastTime >= MIN_TIME_BETWEEN_NOTIFICATIONS_MS) {
          // Avoid duplicates that are currently showing
          if (messages.some((msg) => msg.message.content === content)) return;

          const id = addMessage({
            closable: true,
            content,
            icon: <WarningIcon size={16} />,
            onClick: () => {
              setIgnoreNotificationTypes((prev) => [...prev, notificationType]);
            },
            timeout: 30000,
            type: 'warning',
          });

          switch (notificationType) {
            case 'backgroundEffect':
              setBackgroundEffectsNotificationId(id);
              break;
            case 'noiseCancellation':
              setNoiseCancellationNotificationId(id);
              break;
            case 'encoding':
              setVideoEncodingNotificationId(id);
              break;
          }

          lastNotificationTimeRef.current = now;
        }
        break;
    }
  }, [
    addMessage,
    currentEffect,
    ignoreNotificationTypes,
    messages,
    noiseCancellationOn,
    reason,
    state,
    t,
  ]);
};
